// Context
import { useAuth } from "../../hooks/useAuth";
import { useFeatures } from "../../hooks/useFeatures";
import { useJob } from "../../hooks/useJobs";
import { usePostMessages } from "../../hooks/usePostMessages";
import { usePeptidePricing } from "../../hooks/usePeptidePricing";
import { showPatientUI } from "../../utils/showPatientUI";
import { useCallback, useEffect, useRef, useState } from "react";
import ModalInstallExtension from "../common/ModalInstallExtension";
import ModalHealthCheck from "../common/ModalHealthCheck";
import ModalTicket from "../tickets/ModalTicket";
import ModalDevSettings from "./ModalDevSettings";
import WizardDemoLauncher from "./WizardDemoLauncher";
import OrdersWizardDemoLauncher from "./OrdersWizardDemoLauncher";
import { useLocation, useNavigate } from "react-router";
import ModalAISupport from "../tickets/ModalAISupport";
import { useChats } from "../../hooks/useChats";
import { useIndexDb } from "../../hooks/useIndexDb";
import ModalUserActions from "../chatEdit/modals/ModalUserActions";
import api from "../../services/api";
import { useTranslation } from "react-i18next";
import { Tooltip } from "react-tooltip";
import { buildTrainingUrl } from "../../utils/trainingRouting";
import { useDemo } from "../../hooks/useDemo";
import { requestActiveUnsavedPricingExit } from "../../hooks/useUnsavedPricingGuard";
import { isTrue } from "../../utils/productLineUtils";
import "react-tooltip/dist/react-tooltip.css";

function RightSidebar({ setShowRightSidebar }) {
  const {
    auth,
    logout,
    isMobileApp,
    hasNotifications,
    hasGroup,
    hasPermission,
    changeLocation,
    location,
    isChromeExtensionInstalled,
    home,
    hostName,
    brand,
  } = useAuth();
  const { canUseFeature, hasFeature } = useFeatures();
  const { myJobs, userActions } = useJob();
  const { setChats, setChat } = useChats();
  const { sendShowRecordings } = usePostMessages();
  const { setShowRecordingsModal, recordings } = useIndexDb();
  const pendingRecordingsCount = recordings ? recordings.length : 0;
  const [showModalDevSettings, setShowModalDevSettings] = useState();
  const [showModalTicket, setShowModalTicket] = useState();
  const [showModalAISupport, setShowModalAISupport] = useState();
  const [showModalUserActions, setShowModalUserActions] = useState(false);
  const [showModalHealthCheck, setShowModalHealthCheck] = useState(false);
  const [showTasks, setShowTasks] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [showDemoLauncher, setShowDemoLauncher] = useState(false);
  // "More below" scroll cue: true while the menu can still be scrolled down, so
  // a soft fade + chevron hints there's more (and disappears at the bottom).
  const scrollRef = useRef(null);
  const [moreBelow, setMoreBelow] = useState(false);
  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setMoreBelow(el.scrollHeight - el.scrollTop - el.clientHeight > 8);
  }, []);
  // The drawer stays mounted but `display:none` when closed, so a one-shot
  // measure reads 0. A ResizeObserver re-measures when it becomes visible (size
  // 0 → real on open) and whenever the menu height changes.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === "undefined") {
      checkScroll();
      return;
    }
    const ro = new ResizeObserver(checkScroll);
    ro.observe(el);
    return () => ro.disconnect();
  }, [checkScroll]);
  const { isDemoMode, enterDemo, exitDemo } = useDemo();
  const { isPatient } = usePeptidePricing();

  const isLucasUser = hasFeature("encounters");
  const isOrdersUser = hasFeature("orders") || hasFeature("ordersManagement");
  const isSalesRep = hasGroup("sales-rep") || hasGroup("sales-rep-master");
  // Per-org product line gating (issue #1917). Peptides-only orgs share the
  // `order-provider` plan with DME providers, so we cannot rely on `hasFeature`
  // alone to decide what boards to surface — `auth.orgInfo.productLines` is the
  // source of truth. Be permissive when the field is missing (legacy orgs that
  // pre-date the product-line schema): show DME like before, hide Peptides.
  const productLines = auth?.orgInfo?.productLines;
  const hasDmeProductLine = productLines == null || isTrue(productLines.dme);
  const hasPeptidesProductLine = isTrue(productLines?.peptides);
  const isDMESection =
    pathname === "/orders" ||
    pathname === "/ordersManagement" ||
    pathname === "/salesDashboard" ||
    (pathname === "/" && (home === "ordersManagement" || home === "orders" || home === "salesRep"));
  const isPeptideSection = pathname.startsWith("/peptides") || (pathname === "/" && home === "peptides");

  const logoutFn = () => {
    // Exit demo mode first if active, to restore real session before logout
    if (isDemoMode) {
      exitDemo();
      setShowRightSidebar(false);
      return;
    }
    return requestActiveUnsavedPricingExit(() => {
      setChats([]);
      setChat();
      logout();
      setShowRightSidebar(false);
      navigate("/");
    }, "logout");
  };

  const showRecordings = () => {
    sendShowRecordings();
  };

  const refreshApp = () => {
    window.location.reload();
  };

  const openRequests = () => {
    setShowModalUserActions(true);
    setShowRightSidebar(false);
  };

  const openLinkBackoffice = async () => {
    const userName = auth?.user?.email;
    const brain = localStorage.getItem("organization") || "lucasai";
    const mode = "url";
    const agent = "backoffice";

    const response = await api.getLinkBackoffice(mode, brain, userName, agent);
    if (response?.data?.url) {
      window.location.href = response.data.url;
    }
  };

  const openLinkAdmin = async () => {
    const userName = auth?.user?.email;
    const brain = localStorage.getItem("organization") || "lucasai";
    const mode = "url";
    const agent = "backoffice";

    const response = await api.getLinkBackoffice(mode, brain, userName, agent);
    if (response?.data?.url) {
      window.open(hostName + "/app/org/home", "_blank");
    }
  };

  const goTo = (path) => {
    return requestActiveUnsavedPricingExit(() => {
      navigate(path);
      setShowRightSidebar(false);
    }, "route-navigation");
  };

  const openTrainingWizard = () => {
    const trainingUrl = buildTrainingUrl({
      currentPath: pathname,
      previousPath: sessionStorage.getItem("trainingRedirectPreviousPath") || "",
      hasEncounters: hasFeature("encounters"),
      hasOrders: hasFeature("orders"),
      hasOrdersManagement: hasFeature("ordersManagement"),
      brand,
    });
    window.open(trainingUrl, "_blank");
  };

  // Patient fork — render the trimmed patient sidebar (Profile, Peptide
  // Dashboard, Help) and stop. Provider/admin items below must NEVER reach
  // a patient view; routing this through `showPatientUI` keeps the gate in
  // one place (also covers the dev preview flag). All hooks above are
  // called unconditionally so this early return is Rules-of-Hooks safe.
  if (showPatientUI({ isPatient })) {
    return (
      <PatientSidebar
        auth={auth}
        goTo={goTo}
        showModalTicket={showModalTicket}
        setShowModalTicket={setShowModalTicket}
        onLogout={logoutFn}
        onRefresh={refreshApp}
        isDemoMode={isDemoMode}
        onClose={() => setShowRightSidebar(false)}
      />
    );
  }

  return (
    <div className="right-sidebar" ref={scrollRef} onScroll={checkScroll}>
      <button
        type="button"
        className="drawer-close-btn"
        onClick={() => setShowRightSidebar(false)}
        aria-label={t("rightSidebar.close")}
        title={t("rightSidebar.close")}
      >
        <i className="notranslate material-icons">chevron_right</i>
      </button>
      <div className="sidebar-top">
        <div className="sidebar-avatar">
          {`${auth?.user?.firstName?.[0] || ""}${auth?.user?.lastName?.[0] || ""}`.toUpperCase() || (
            <i className="notranslate material-icons">person</i>
          )}
        </div>
        <div className="title" title={auth?.user?.lastName + " " + auth?.user?.firstName}>
          {t("rightSidebar.welcome", { lastName: auth?.user?.lastName })}
        </div>
        <div className="org-name" title={auth?.session?.currentOrganizationName}>
          {auth?.orgInfo?.officeName || auth?.session?.currentOrganizationName}
        </div>
        <div className="badge">{t("rightSidebar.tasks", { count: Object.keys(myJobs).length })}</div>

        {Object.keys(myJobs).length > 0 && (
          <div className="mt-10 mb-10 flex-center">
            {!showTasks ? (
              <div className="link" onClick={() => setShowTasks(true)}>
                {t("rightSidebar.showTasks", { count: Object.keys(myJobs).length })}
              </div>
            ) : (
              <div className="link" onClick={() => setShowTasks(false)}>
                {t("rightSidebar.hideTasks", { count: Object.keys(myJobs).length })}
              </div>
            )}

            <div>
              {showTasks &&
                Object.keys(myJobs).map((chatId, j) => (
                  <div key={j} className="job-title badge">
                    {t("rightSidebar.generationInProgress", { id: chatId })}
                  </div>
                ))}
            </div>
          </div>
        )}

        {Object.keys(userActions).length > 0 && (
          <button className="mt-20" onClick={openRequests}>
            {t("rightSidebar.requests", { count: Object.keys(userActions).length })}
          </button>
        )}
      </div>
      <div className="right-sidebar-content">
        {auth.user.locations && auth.user.locations.length > 0 && (
          <div className="m-10 mobile">
            <select
              value={location}
              onChange={(e) => {
                if (canUseFeature("locations")) {
                  changeLocation(e.target.value);
                }
              }}
              disabled={auth?.user?.locations?.length === 1}
            >
              {auth.user.locations.map((loc, k) => (
                <option value={loc.id} key={"loc_" + k}>
                  {loc.name}
                </option>
              ))}
              {auth?.user?.locations?.length > 1 && (
                <option value="" key={"loc_unassgned"}>
                  {t("common.all")}
                </option>
              )}
            </select>
          </div>
        )}

        {hasFeature("encounters") && (
          <div>
            <div className={"right-sidebar-row" + (pathname === "/" ? " disabled" : "")} onClick={() => goTo("/")}>
              <i className="notranslate material-symbols-outlined">stethoscope</i>
              {t("rightSidebar.encounters")}
            </div>
          </div>
        )}

        {hasFeature("ordersSalesDashboard") && (isSalesRep || hasGroup("supervisor")) && (
          <div
            className={
              "right-sidebar-row" +
              (pathname === "/salesDashboard" || (home === "salesRep" && pathname === "/") ? " disabled" : "")
            }
            onClick={() => goTo("/salesDashboard")}
          >
            <i className="notranslate material-icons-outlined">attach_money</i>
            {t("rightSidebar.salesDashboard")}
          </div>
        )}

        {isOrdersUser && hasDmeProductLine && (!isSalesRep || auth?.user.role === "administrator") && (
          <div>
            <div
              className={
                "right-sidebar-row" +
                (pathname === "/orders" ||
                pathname === "/ordersManagement" ||
                (home === "ordersManagement" && pathname === "/") ||
                (home === "orders" && pathname === "/")
                  ? " disabled"
                  : "")
              }
              onClick={() => goTo(hasFeature("ordersManagement") ? "/ordersManagement" : "/orders")}
            >
              <i className="notranslate material-icons-outlined">shopping_cart</i>
              {t("rightSidebar.dmeDashboard")}
            </div>
          </div>
        )}

        {hasPeptidesProductLine && (!isSalesRep || auth?.user.role === "administrator") && (
          <div
            className={
              "right-sidebar-row" +
              (pathname.startsWith("/peptides") || (home === "peptides" && pathname === "/") ? " disabled" : "")
            }
            onClick={() => goTo("/peptides?view=orders")}
          >
            <i className="notranslate material-icons-outlined">science</i>
            {t("rightSidebar.peptideDashboard")}
          </div>
        )}

        {(isDMESection || isPeptideSection || isDemoMode) && (
          <div
            className="right-sidebar-row"
            onClick={() => {
              if (isDemoMode) {
                exitDemo();
              } else {
                enterDemo();
              }
              setShowRightSidebar(false);
            }}
          >
            <i className="notranslate material-icons-outlined">{isDemoMode ? "stop_circle" : "play_circle"}</i>
            {isDemoMode ? t("rightSidebar.exitDemo") : t("rightSidebar.demoMode")}
          </div>
        )}

        {hasGroup("team-member", true) && (
          <div>
            <div
              className={"right-sidebar-row" + (pathname === "/onboarding" ? " disabled" : "")}
              onClick={() => goTo("/onboarding")}
            >
              <i className="notranslate material-icons-outlined">person_add</i>
              {t("rightSidebar.onboarding")}
            </div>
          </div>
        )}

        {false && hasFeature("ordersManagement") && (
          <div
            className={"right-sidebar-row" + (pathname === "/dme-credentialing" ? " disabled" : "")}
            onClick={() => goTo("/dme-credentialing")}
          >
            <i className="notranslate material-icons-outlined">badge</i>
            {t("rightSidebar.dmeCredentialing", "Credentialing")}
          </div>
        )}

        {hasFeature("ordersManagement") && (
          <div className="right-sidebar-row" onClick={() => setShowDemoLauncher(true)}>
            <i className="notranslate material-icons-outlined">school</i>
            {t("rightSidebar.onboardingWizardSimulator")}
          </div>
        )}

        {isMobileApp ? (
          <div className="right-sidebar-row" onClick={showRecordings}>
            <i className="notranslate material-icons-outlined">graphic_eq</i>
            {t("rightSidebar.localRecordings")}
          </div>
        ) : (
          <div
            className="right-sidebar-row"
            onClick={() => {
              setShowRecordingsModal(true);
              setShowRightSidebar(false);
            }}
          >
            <i className="notranslate material-icons-outlined">graphic_eq</i>
            {t("rightSidebar.localRecordings")}
            {pendingRecordingsCount > 0 && <span className="recordings-badge">{pendingRecordingsCount}</span>}
          </div>
        )}
        <div
          className={"right-sidebar-row" + (pathname === "/settings/profile" ? " disabled" : "")}
          onClick={() => goTo("/settings/profile")}
        >
          <i className="notranslate material-icons-outlined">account_circle</i>
          {t("rightSidebar.profile")}
        </div>

        {isLucasUser && (
          <>
            <div
              className={"right-sidebar-row" + (pathname === "/settings/preferences" ? " disabled" : "")}
              onClick={() => goTo("/settings/preferences")}
            >
              <i className="notranslate material-icons-outlined">settings</i>
              {t("rightSidebar.preferences")}
            </div>

            <div
              className={"right-sidebar-row" + (pathname === "/patients" ? " disabled" : "")}
              onClick={() => goTo("/patients")}
            >
              <i className="notranslate material-icons-outlined">group</i>
              {t("rightSidebar.patientPanel")}
            </div>

            <div
              className={"right-sidebar-row" + (pathname === "/settings/notesSettings" ? " disabled" : "")}
              onClick={() => goTo("/settings/notesSettings")}
            >
              <i className="notranslate material-icons-outlined">assignment</i>
              {t("rightSidebar.notesSettings")}
            </div>

            {hasGroup("supervisor") && hasFeature("encounters") && (
              <div
                className={"right-sidebar-row" + (pathname === "/settings/codeOrdering" ? " disabled" : "")}
                onClick={() => goTo("/settings/codeOrdering")}
              >
                <i className="notranslate material-icons-outlined">format_list_numbered</i>
                {t("rightSidebar.codeOrdering", "Code Ordering")}
              </div>
            )}
          </>
        )}

        {hasFeature("encounters") && (
          <div className={"right-sidebar-row" + (pathname === "/ai-agent" ? " disabled" : "")} onClick={() => goTo("/ai-agent")}>
            <i className="notranslate material-icons-outlined">psychology</i>
            {t("rightSidebar.aiAgent")}
          </div>
        )}

        {(hasGroup("supervisor") ||
          hasGroup("team-member") ||
          isLucasUser ||
          auth?.user?.role === "administrator" ||
          hasFeature("debug")) && <h4> {t("rightSidebar.admin")} </h4>}

        {(hasGroup("supervisor") || hasGroup("team-member")) && (
          <div
            className={"right-sidebar-row" + (pathname === "/users" ? " disabled" : "")}
            onClick={() => canUseFeature("team") && goTo("/users")}
          >
            <i className="notranslate material-icons-outlined">groups</i>
            {t("rightSidebar.userManagement")}
          </div>
        )}

        {isLucasUser && (
          <div className="right-sidebar-row" onClick={openLinkBackoffice}>
            <i className="notranslate material-icons-outlined">admin_panel_settings</i>
            {t("rightSidebar.classicApp")}
          </div>
        )}

        {/* hasGroup("supervisor") is also true for administrators, so both see My Plan (Ultimate included). */}
        {hasGroup("supervisor") &&
          auth.session.currentOrganizationPlan !== "order-manufacturer" &&
          (auth.session.currentOrganizationPlan !== "order-provider" || hasFeature("encounters")) && (
            <div className={"right-sidebar-row" + (pathname === "/my-plan" ? " disabled" : "")} onClick={() => goTo("/my-plan")}>
              <i className="notranslate material-icons-outlined">card_membership</i>
              {t("rightSidebar.myPlan", "My Plan")}
            </div>
          )}

        {auth?.user?.role === "administrator" && (
          <div className="right-sidebar-row" onClick={openLinkAdmin}>
            <i className="notranslate material-symbols-outlined">code_blocks</i>
            {t("rightSidebar.adminPortal")}
          </div>
        )}

        {isLucasUser && (auth.user.role === "administrator" || auth.user.role === "supervisor") && (
          <div
            className={"right-sidebar-row" + (pathname === "/ehrTemplates" ? " disabled" : "")}
            onClick={() => {
              if (!isChromeExtensionInstalled) {
                setShowInstallModal(true);
              } else {
                goTo("/ehrTemplates");
              }
            }}
          >
            <i className="notranslate material-icons-outlined">sync</i>
            {t("rightSidebar.ehrIntegration")}
          </div>
        )}

        {/* Internal gate (canLaunchDemo: beta org OR healcore brand) covers any
            user — encounters, orders, or patient. Don't double-gate on
            isLucasUser since the launcher now demos the patient flow too. */}
        <WizardDemoLauncher />

        {isLucasUser && hasGroup("supervisor") && (
          <div
            className={"right-sidebar-row" + (pathname === "/locations" ? " disabled" : "")}
            onClick={() => canUseFeature("locations") && goTo("/locations")}
          >
            <i className="notranslate material-icons-outlined">domain</i>
            {t("rightSidebar.locations")}
          </div>
        )}

        {isLucasUser && hasPermission("lucas.analytics") && (
          <div
            className={"right-sidebar-row" + (pathname === "/analytics" ? " disabled" : "")}
            onClick={() => goTo("/analytics")}
          >
            <i className="notranslate material-icons-outlined">equalizer</i>
            {t("rightSidebar.analytics")}
          </div>
        )}

        {hasFeature("debug") && !isDemoMode && (
          <div className="right-sidebar-row" onClick={() => setShowModalDevSettings(true)}>
            <i className="notranslate material-icons-outlined">code</i>
            {t("rightSidebar.devSettings")}
          </div>
        )}

        {isLucasUser && hasGroup("supervisor") && (
          <div
            className={"right-sidebar-row" + (pathname === "/settings/qcodes" ? " disabled" : "")}
            onClick={() => canUseFeature("qCode") && goTo("/settings/qcodes")}
          >
            <i className="notranslate material-icons-outlined">assignment</i>
            {t("rightSidebar.qcodes")}
          </div>
        )}
        <h4> {t("rightSidebar.help")} </h4>
        <div className="right-sidebar-row" onClick={() => setShowModalTicket(true)}>
          {hasNotifications && <div className="badge-to-read"></div>}
          <i className="notranslate material-icons-outlined">help_outline</i>
          {t("rightSidebar.help")}
        </div>

        <div className="right-sidebar-row" onClick={() => setShowModalHealthCheck(true)}>
          <i className="notranslate material-icons-outlined">health_and_safety</i>
          {t("rightSidebar.systemHealthCheck")}
        </div>

        <div className="right-sidebar-row" onClick={() => setShowModalAISupport(true)}>
          <i className="notranslate material-icons-outlined">support_agent</i>
          {t("rightSidebar.aiSupport")}
        </div>

        {auth.session?.currentOrganizationPlan !== "order-manufacturer" && (
          <div className="right-sidebar-row" onClick={openTrainingWizard}>
            <i className="notranslate material-icons-outlined">model_training</i>
            {t("rightSidebar.trainingWizard")}
          </div>
        )}
        {/* <div className="right-sidebar-row">
          <i className="notranslate material-icons-outlined">settings</i>
          Preferences
        </div>
        <div className="right-sidebar-row">
          <i className="notranslate material-icons-outlined">patient_list</i>
          Patient Panel
        </div>
        <div className="right-sidebar-row">
          <i className="notranslate material-icons-outlined">share</i>
          Tell Your Friends
        </div>
        <div className="right-sidebar-row">
          <i className="notranslate material-icons-outlined">info</i>
          About LucasAI
        </div>
         */}

        <div className="right-sidebar-row" onClick={refreshApp}>
          <i className="notranslate material-icons-outlined">refresh</i>
          {t("rightSidebar.refreshApp")}
        </div>

        {/* Hide logout while in demo mode — users should exit demo first via the
            DemoBanner "Exit Demo" button, then logout normally. This eliminates
            the foot-gun where a logout click could fire any demo-state-dependent
            cleanup against the real session. */}
        {!isDemoMode && (
          <div onClick={logoutFn} className="right-sidebar-row">
            <i className="notranslate material-icons-outlined">logout</i>
            {t("rightSidebar.logout")}
          </div>
        )}
      </div>

      {showModalDevSettings && (
        <ModalDevSettings
          show={showModalDevSettings}
          close={() => {
            setShowRightSidebar(false);
            setShowModalDevSettings(false);
          }}
        ></ModalDevSettings>
      )}

      <ModalTicket show={showModalTicket} close={() => setShowModalTicket(false)}></ModalTicket>

      <ModalAISupport show={showModalAISupport} close={() => setShowModalAISupport(false)}></ModalAISupport>

      <ModalUserActions show={showModalUserActions} close={() => setShowModalUserActions(false)}></ModalUserActions>

      <ModalHealthCheck show={showModalHealthCheck} close={() => setShowModalHealthCheck(false)} brand={brand} />

      <ModalInstallExtension show={showInstallModal} close={() => setShowInstallModal(false)} />

      <Tooltip id="my-tooltip" style={{ maxWidth: "240px", zIndex: 10000 }} />

      {showDemoLauncher && <OrdersWizardDemoLauncher close={() => setShowDemoLauncher(false)} />}

      <div className={"scroll-more-cue" + (moreBelow ? " show" : "")} aria-hidden="true">
        <i className="notranslate material-icons">keyboard_arrow_down</i>
      </div>
    </div>
  );
}

function PatientSidebar({ auth, goTo, showModalTicket, setShowModalTicket, onLogout, onRefresh, isDemoMode, onClose }) {
  const { t } = useTranslation();
  return (
    <div className="right-sidebar">
      <button
        type="button"
        className="drawer-close-btn"
        onClick={() => onClose?.()}
        aria-label={t("rightSidebar.close")}
        title={t("rightSidebar.close")}
      >
        <i className="notranslate material-icons">chevron_right</i>
      </button>
      <div className="sidebar-top">
        <div className="sidebar-avatar">
          {`${auth?.user?.firstName?.[0] || ""}${auth?.user?.lastName?.[0] || auth?.user?.name?.[0] || ""}`.toUpperCase() || (
            <i className="notranslate material-icons">person</i>
          )}
        </div>
        <div className="title" title={(auth?.user?.lastName || "") + " " + (auth?.user?.firstName || "")}>
          {t("rightSidebar.welcome", { lastName: auth?.user?.lastName || auth?.user?.name || "" })}
        </div>
        {(auth?.orgInfo?.officeName || auth?.session?.currentOrganizationName) && (
          <div className="org-name" title={auth?.session?.currentOrganizationName}>
            {auth?.orgInfo?.officeName || auth?.session?.currentOrganizationName}
          </div>
        )}
      </div>
      <div className="right-sidebar-content">
        <div className="right-sidebar-row" onClick={() => goTo("/settings/profile")}>
          <i className="notranslate material-icons-outlined">account_circle</i>
          {t("rightSidebar.profile")}
        </div>
        <div className="right-sidebar-row" onClick={() => goTo("/peptides?view=home")}>
          <i className="notranslate material-icons-outlined">science</i>
          {t("rightSidebar.peptideDashboard")}
        </div>
        <h4> {t("rightSidebar.help")} </h4>
        <div className="right-sidebar-row" onClick={() => setShowModalTicket(true)}>
          <i className="notranslate material-icons-outlined">help_outline</i>
          {t("rightSidebar.help")}
        </div>
        <div className="right-sidebar-row" onClick={onRefresh}>
          <i className="notranslate material-icons-outlined">refresh</i>
          {t("rightSidebar.refreshApp")}
        </div>
        {/* Hide logout while in demo mode — see comment on the non-patient
            sidebar's logout row for rationale. */}
        {!isDemoMode && (
          <div className="right-sidebar-row" onClick={onLogout}>
            <i className="notranslate material-icons-outlined">logout</i>
            {t("rightSidebar.logout")}
          </div>
        )}
      </div>
      <ModalTicket show={showModalTicket} close={() => setShowModalTicket(false)} />
    </div>
  );
}

export default RightSidebar;
